import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { Cell } from 'api';
import { useXlsxSpreadsheetNotesQuery } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/useXlsxSpreadsheetNotesQuery';
import { XLSX_VIEWER_STRINGS } from 'components/Drawer/document-preview-drawer-tab/xlsx-viewer/xlsx-viewer.contants';
import { GridSkeleton } from 'components/Grid';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import useSpreadsheetNoteTabs from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs';
import { TabsNavigation, Tab as TabType } from 'components/TabsNavigation';
import { useTabsMenu } from 'components/TabsNavigation/hooks/useTabsMenu';
import MappingActionsToolbar from './MappingActionsToolbar';
import { clearPendingNavigation, getPendingNavigation } from './pendingNavigation';
import { XLSXViewerTabProps } from './XLSXViewerTab.props';
import { EventType, useEventSubscription } from '../../../../context';
import { NavigateToDocumentReferencePayload } from '../../../../context/EventBusContext/EventBusContext.type';
import { AttributeMappingType } from '../../../../pages/Financials/financials-widget-drawer/types';
import { FinancialsCellWithReferences } from '../../../../pages/Financials/types/cell-with-references';
import { useAcceptAllAIMappings, useDeclineAllAIMappings } from '../../../../services/hooks/attributeMapper';
import SheetAttributeManager from '../../../../services/sheet-attribute-manager/SheetAttributeManager';
import { useCellMappingStore } from '../../../../store';
import { useAttributeMapperStore } from '../../../../store/useAttributeMapperStore';
import { Logger } from '../../../../utilities';
import adjustAlphaFromColorString from '../../../../utilities/adjust-alpha-in-color-string/adjust-alpha-from-color-string';
import { convertedColors } from '../../../ScalarSpreadsheet/constants';
import { useAttributeMappedWidgetDrawer } from '../../attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';

const XLSXViewerTab = ({ document }: XLSXViewerTabProps) => {
  const {
    attributeMapper,
    attributeReferenceConfig,
    handlePushingAttributeMappings,
    handleClearMappings,
    onReprocessDocument,
    onAcceptAllAIFindings,
    hasPendingAIFindings,
  } = useAttributeMappedWidgetDrawer();
  const { updateCellMapping } = useCellMappingStore();
  const { setAttributeMapperManager } = useAttributeMapperStore();
  const [acceptAllAIMappings] = useAcceptAllAIMappings();
  const [declineAllAIMappings] = useDeclineAllAIMappings();
  const [activeTab, setActiveTab] = useState<TabType | null>(null);
  const [controlledTabIndex, setControlledTabIndex] = useState<number | null>(null);
  const controlledTabResetRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleReprocess = useCallback(() => {
    onReprocessDocument?.(document);
  }, [onReprocessDocument, document]);
  const { data, error, isLoading, isPolling } = useXlsxSpreadsheetNotesQuery(document.file.id);

  // pass notes into the store

  const notes = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return data;
  }, [data]);

  const hasPendingAISuggestions = useMemo(
    () => attributeMapper?.hasUnacceptedAIMappings() || hasPendingAIFindings || false,
    [attributeMapper, hasPendingAIFindings]
  );

  const handleAcceptAllAISuggestions = useCallback(async () => {
    const currentMappingId = attributeMapper?.sheetAttributeMapping.id;
    await Promise.all([
      currentMappingId && hasPendingAISuggestions
        ? acceptAllAIMappings(currentMappingId)
          .then(updatedMapping => setAttributeMapperManager(new SheetAttributeManager(updatedMapping)))
          .catch((error: Error) => Logger.error('XLSXViewerTab - Error accepting all AI mappings', error))
        : Promise.resolve(),
      onAcceptAllAIFindings?.().catch((error: Error) =>
        Logger.error('XLSXViewerTab - Error accepting all AI findings', error)
      ),
    ]);
  }, [attributeMapper, hasPendingAISuggestions, acceptAllAIMappings, setAttributeMapperManager, onAcceptAllAIFindings]);

  const handleDeclineAllAISuggestions = useCallback(async () => {
    const currentMappingId = attributeMapper?.sheetAttributeMapping.id;
    if (!currentMappingId) {
      return;
    }
    try {
      const updatedMapping = await declineAllAIMappings(currentMappingId);
      setAttributeMapperManager(new SheetAttributeManager(updatedMapping));
    } catch (error) {
      Logger.error('XLSXViewerTab - Error declining all AI mappings', error);
    }
  }, [attributeMapper, declineAllAIMappings, setAttributeMapperManager]);

  const { handleMenuOpen, handleMenuClose, menuAnchorEl } = useTabsMenu();

  const isAttributeMapperEnabled = !!attributeMapper;

  const { tabs, workbookContextValue, onNoteChange, cells } = useSpreadsheetNoteTabs({
    notes,
    actionsAreDisabled: false,
    isAttributeMapperEnabled,
    attributeReferenceConfig,
    attributeMapper,
    documentId: document.document_reference_id,
  });

  const handleActiveTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const normalizationRegex = useMemo(() => /[^a-z0-9]/g, []);

  const navigateToSheet = useCallback(
    (sheetName: string) => {
      if (!tabs?.length || !sheetName) {
        return;
      }
      const normalizedTarget = sheetName.toLowerCase().replace(normalizationRegex, '');
      const targetIndex = tabs.findIndex(tab => {
        const label = typeof tab.label === 'string' ? tab.label : '';
        return label.toLowerCase().replace(normalizationRegex, '') === normalizedTarget;
      });
      if (targetIndex !== -1) {
        setControlledTabIndex(targetIndex);
        clearTimeout(controlledTabResetRef.current);
        controlledTabResetRef.current = setTimeout(() => setControlledTabIndex(null), 100);
      }
    },
    [tabs, normalizationRegex]
  );

  useEventSubscription(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, (payload: NavigateToDocumentReferencePayload) => {
    if (payload.documentReferenceId !== document.document_reference_id) {
      return;
    }
    if (payload.sheetName) {
      navigateToSheet(payload.sheetName);
    }
  });

  useEffect(() => {
    const pending = getPendingNavigation();
    if (pending && pending.documentReferenceId === document.document_reference_id) {
      if (pending.sheetName) {
        navigateToSheet(pending.sheetName);
      }
      clearPendingNavigation();
    }
  }, [tabs, document.document_reference_id, navigateToSheet]);

  const clearHighlights = () => {
    if (!cells) {
      return;
    }

    Object.values(cells).forEach(sheet => {
      Object.values(sheet).forEach(cell => {
        cell.setCellBackgroundColor(undefined);
        // eslint-disable-next-line no-param-reassign
        cell.style = undefined;
        cell.dispatchEvent();
      });
    });
  };

  useEventSubscription(
    EventType.SPREADSHEET_ACTIVE_CELL_CHANGED,
    ([newCell, _prevCell]: [{ cellInfo: FinancialsCellWithReferences }, FinancialsCellWithReferences]) => {
      if (attributeReferenceConfig?.selectionMode === AttributeMappingType.SINGLE_CELL && attributeMapper) {
        Logger.debug('Handling single cell avoiding highlights');
        handlePushingAttributeMappings?.({
          documentReferencePayload: {
            documentReferenceId: document.document_reference_id,
            sheet: `${activeTab?.label}`,
          },
          selectedCell: newCell,
        });
      }

      clearHighlights();

      if (!cells || !newCell.cellInfo.mappingReferences?.length) {
        return;
      }
      const normalizationRegex = /[^a-z0-9]/g;

      // normalize the sheet names here to use as a look up for the original sheet name, to grab cells correctly
      const normalizedToOriginal = Object.keys(cells).reduce(
        (acc, sheetName) => {
          const normalized = sheetName.toLowerCase().replaceAll(normalizationRegex, '');
          acc[normalized] = sheetName;
          return acc;
        },
        {} as Record<string, string>
      );

      newCell.cellInfo.mappingReferences.forEach(item => {
        // the sheet names are normalized outside of this context upstream
        // in order to ensure we can find the cells to highlight on the right sheet
        // the name must be normalized
        const normalizedSheetName = item.sheet_name.toLowerCase().replaceAll(normalizationRegex, '');
        const originalSheetName = normalizedToOriginal[normalizedSheetName];
        const noteForUpdatedSheet = originalSheetName && cells[originalSheetName];
        if (noteForUpdatedSheet) {
          item.cell_references.forEach((cellRef, index) => {
            const rangeMatch = cellRef.ref.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i);
            const cellRefs: string[] = [];

            // cellRef.ref could be a range of cells as well such as A15:A19
            if (rangeMatch) {
              const [, startCol, startRowStr, , endRowStr] = rangeMatch;
              const lowestRowNumber = Math.min(+startRowStr, +endRowStr);
              const highestRowNumber = Math.max(+startRowStr, +endRowStr);
              for (let rowNum = lowestRowNumber; rowNum <= highestRowNumber; rowNum++) {
                cellRefs.push(`${startCol.toUpperCase()}${rowNum}`);
              }
            } else {
              cellRefs.push(cellRef.ref);
            }

            const color = convertedColors[index % convertedColors.length];
            const borderColor = adjustAlphaFromColorString(color, 0.6);
            cellRefs.forEach(ref => {
              const updatedCell = noteForUpdatedSheet?.[ref];
              if (!updatedCell) {
                return;
              }
              updatedCell.setCellBackgroundColor(color);
              updatedCell.style = { boxShadow: `inset 0 0 0 3px ${borderColor}` };
              updatedCell.dispatchEvent();
            });
          });
        }
      });
    }
  );

  useEffect(() => {
    //   we return here to prevent updating the cell mapping when we don't
    // actually have any data. Also prevents bugs on initial load of sheet
    if (isLoading) {
      return;
    }
    // POC of propagating the cell mapping to the attribute mapper
    // Include document ID in the key to distinguish sheets from different documents with the same name
    const documentId = document.document_reference_id;
    const cellMapping = notes
      .filter(note => note.content)
      .reduce(
        (acc, note) => {
          const { cells: mappedCells } = note.content;
          const name = note.name ?? '';
          // Use composite key: documentId_sheetName to avoid conflicts when multiple documents have the same sheet name
          const compositeKey = `${documentId}_${name}`;
          acc[compositeKey] = mappedCells ?? {};
          // Also keep the original key for backward compatibility
          acc[name] = mappedCells ?? {};
          return acc;
        },
        {} as Record<string, Record<string, Cell>>
      );

    updateCellMapping(cellMapping);
  }, [notes, updateCellMapping, isLoading, document.document_reference_id]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        handlePushingAttributeMappings?.({
          documentReferencePayload: {
            documentReferenceId: document.document_reference_id,
            sheet: `${activeTab?.label}`,
          },
        });
      }
    },
    [handlePushingAttributeMappings, activeTab?.label, document.document_reference_id]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const hasCapTableReference: boolean = useMemo(() => {
    const hasCaptableRef = document.referenced_in?.find(refs => refs.reference_object_type === 'Cap Table');
    return !!hasCaptableRef;
  }, [document]);

  if (isLoading && !error && !isPolling) {
    return <GridSkeleton />;
  }

  if (isPolling) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={200} gap={2}>
        <div>{XLSX_VIEWER_STRINGS.LOADING.MESSAGE}</div>
        <GridSkeleton />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Alert severity="error" variant="filled">
          <strong>{XLSX_VIEWER_STRINGS.ERROR.TITLE}</strong>
          <div>{XLSX_VIEWER_STRINGS.ERROR.MESSAGE}</div>
        </Alert>
      </Box>
    );
  }

  return (
    <WorkbookContext.Provider value={workbookContextValue}>
      <TabsNavigation
        menuAnchorEl={menuAnchorEl}
        mountOnActive
        handleMenuClose={handleMenuClose}
        handleMenuOpen={handleMenuOpen}
        relativeDrawer
        rightAction={
          isAttributeMapperEnabled && handleClearMappings ? (
            <MappingActionsToolbar
              documentReferenceId={document.document_reference_id}
              activeSheetLabel={activeTab?.label as string | undefined}
              onPushMappings={() => handlePushingAttributeMappings?.()}
              onClearMappings={handleClearMappings}
              hasPendingAISuggestions={hasPendingAISuggestions}
              onAcceptAllAISuggestions={handleAcceptAllAISuggestions}
              onDeclineAllAISuggestions={handleDeclineAllAISuggestions}
              onReprocessDocument={handleReprocess}
              showCreateLabels={!hasCapTableReference}
            />
          ) : undefined
        }
        tabs={tabs}
        onActiveTabChange={handleActiveTabChange}
        controlledActiveTab={controlledTabIndex}
      />
    </WorkbookContext.Provider>
  );
};
export default XLSXViewerTab;
